// pdf.js - PDF 변환

import { getPaperSize } from './scan-settings.js';

const { jsPDF } = window.jspdf;

// 용지 크기 (mm)
const PAPER_SIZES = {
  a4: { w: 210, h: 297 },
  letter: { w: 215.9, h: 279.4 }
};

const MARGIN = 10;
const PDF_DPI = 200; // PDF 출력 해상도 (200dpi = 좋은 품질, 일반 문서용)
const JPEG_QUALITY = 0.85;

/**
 * 현재 설정에 맞는 용지 크기 반환
 */
function getCurrentPaperSize() {
  const key = getPaperSize();
  return PAPER_SIZES[key] || PAPER_SIZES.a4;
}

/**
 * 이미지를 PDF로 변환
 * @param {string} imageDataURL - 이미지 dataURL
 * @param {Object} options - 옵션
 * @returns {Promise<Blob>} PDF Blob
 */
export async function imageToPdf(imageDataURL, options = {}) {
  return new Promise((resolve, reject) => {
    try {
      const img = new Image();

      img.onload = () => {
        try {
          const paper = getCurrentPaperSize();
          const format = getPaperSize() === 'letter' ? 'letter' : 'a4';

          // PDF 생성 (세로 방향)
          const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: format
          });

          // 이미지 크기 계산 (용지에 맞게)
          const { width, height } = calculateImageSize(
            img.width,
            img.height,
            paper.w - (MARGIN * 2),
            paper.h - (MARGIN * 2)
          );

          // 이미지 중앙 정렬
          const x = (paper.w - width) / 2;
          const y = (paper.h - height) / 2;

          // PDF용으로 리샘플링 (크기 최적화)
          const resampledDataURL = resampleForPdf(img, width, height);

          // 이미지 추가 (압축 적용)
          pdf.addImage(resampledDataURL, 'JPEG', x, y, width, height, undefined, 'FAST');

          // Blob으로 변환
          const pdfBlob = pdf.output('blob');
          resolve(pdfBlob);
        } catch (e) {
          reject(new Error('PDF 생성 실패: ' + e.message));
        }
      };

      img.onerror = () => {
        reject(new Error('이미지 로드 실패'));
      };

      img.src = imageDataURL;
    } catch (e) {
      reject(new Error('PDF 변환 실패: ' + e.message));
    }
  });
}

/**
 * 다중 이미지를 다중 페이지 PDF로 변환
 * @param {string[]} imageDataURLs - 이미지 dataURL 배열
 * @returns {Promise<Blob>} PDF Blob
 */
export async function imagesToPdf(imageDataURLs) {
  if (!imageDataURLs || imageDataURLs.length === 0) {
    throw new Error('변환할 이미지가 없습니다.');
  }

  // 모든 이미지를 먼저 로드
  const images = await Promise.all(
    imageDataURLs.map(dataURL => loadImage(dataURL))
  );

  const paper = getCurrentPaperSize();
  const format = getPaperSize() === 'letter' ? 'letter' : 'a4';

  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: format
  });

  images.forEach((img, index) => {
    if (index > 0) {
      pdf.addPage();
    }

    const { width, height } = calculateImageSize(
      img.width,
      img.height,
      paper.w - (MARGIN * 2),
      paper.h - (MARGIN * 2)
    );

    const x = (paper.w - width) / 2;
    const y = (paper.h - height) / 2;

    // PDF용으로 리샘플링 (크기 최적화)
    const resampledDataURL = resampleForPdf(img, width, height);

    // 이미지 추가 (압축 적용)
    pdf.addImage(resampledDataURL, 'JPEG', x, y, width, height, undefined, 'FAST');
  });

  return pdf.output('blob');
}

/**
 * 이미지 로드 헬퍼
 */
function loadImage(dataURL) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('이미지 로드 실패'));
    img.src = dataURL;
  });
}

/**
 * 이미지를 PDF용으로 리샘플링 (JPEG 압축)
 * @param {HTMLImageElement} img - 원본 이미지
 * @param {number} targetWidthMM - PDF 내 목표 너비 (mm)
 * @param {number} targetHeightMM - PDF 내 목표 높이 (mm)
 * @returns {string} 리샘플링된 JPEG dataURL
 */
function resampleForPdf(img, targetWidthMM, targetHeightMM) {
  // mm를 픽셀로 변환 (DPI 기준)
  const targetWidthPx = Math.round(targetWidthMM * PDF_DPI / 25.4);
  const targetHeightPx = Math.round(targetHeightMM * PDF_DPI / 25.4);

  // 원본 이미지 비율 유지하며 목표 크기에 맞춤
  const ratio = Math.min(targetWidthPx / img.width, targetHeightPx / img.height);
  const outputWidth = Math.round(img.width * ratio);
  const outputHeight = Math.round(img.height * ratio);

  // 원본이 이미 작으면 리샘플링 불필요
  if (ratio >= 1) {
    // 하지만 JPEG 압축은 적용
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0);
    return canvas.toDataURL('image/jpeg', JPEG_QUALITY);
  }

  // 리샘플링
  const canvas = document.createElement('canvas');
  canvas.width = outputWidth;
  canvas.height = outputHeight;
  const ctx = canvas.getContext('2d');

  // 고품질 리샘플링
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, outputWidth, outputHeight);

  return canvas.toDataURL('image/jpeg', JPEG_QUALITY);
}

/**
 * 이미지 크기 계산 (비율 유지하며 최대 크기에 맞춤)
 */
function calculateImageSize(imgWidth, imgHeight, maxWidth, maxHeight) {
  const ratio = Math.min(maxWidth / imgWidth, maxHeight / imgHeight);

  return {
    width: imgWidth * ratio,
    height: imgHeight * ratio
  };
}

/**
 * 파일명 생성
 */
export function generateFileName() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hour = String(now.getHours()).padStart(2, '0');
  const minute = String(now.getMinutes()).padStart(2, '0');
  const second = String(now.getSeconds()).padStart(2, '0');

  return `scan_${year}${month}${day}_${hour}${minute}${second}.pdf`;
}
